import { Grid, Typography } from "@mui/material"
import React, { useState } from "react"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { InfoBox } from "../../../../../merchant/admin/shops/components/formElements/InfoBox"
import { GDPRFilterUserTypes, GDPRUserDeletionPeriod } from "../gdprTypes"
import { Button } from "../../../../../office/admin/components/Button"
import { UsersByDomainExport } from "./UsersByDomainExport"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { fetchGDPRUsersByDomain, fetchGDPRVerifiedDomains, GDPRFilesQuery } from "../gdprApi"
import { useExportUsersData } from "../hooks/useExportUsersData"
import { useSupplier } from "../../../../supplierHooks"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"

const useStyles = makeStyles()((theme) => ({
    container: {
        borderRadius: ".75rem",
        padding: "2.5rem",
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
        border: "1px solid rgba(226, 232, 240, 1)",
        background: theme.palette.common.white,
    },
    title: {
        fontSize: "1.5rem",
        fontWeight: 400,
    },
    button: {
        width: "fit-content",
    },
}))

export function GDPRExportsTab() {
    const { classes } = useStyles()
    const [userTypesFilter, setUserTypesFilter] = useState<Array<string>>([GDPRFilterUserTypes.ADMINISTRATOR])
    const [periodInDaysFilter, setPeriodInDaysFilter] = useState(GDPRUserDeletionPeriod.DAYS_7)
    const [includeDeletedFilter, setIncludeDeletedFilter] = useState(false)
    const { supplierId } = useSupplier()

    const { data: verifiedDomains } = useApiQuery(fetchGDPRVerifiedDomains, {
        queryName: GDPRFilesQuery.GDPR_VERIFIED_DOMAINS,
        dependencies: {
            supplierId,
        },
        select: (data) => data.domains || [],
    })

    const { mutateAsync: fetchUsers, isLoading } = useApiMutation(fetchGDPRUsersByDomain, {})

    const { handleExportRows } = useExportUsersData()

    async function handleExport() {
        const data = await fetchUsers({
            supplierId,
            userTypes: userTypesFilter,
            includeDeletedUsers: includeDeletedFilter,
            periodInDays: periodInDaysFilter,
        })
        handleExportRows(data, userTypesFilter, includeDeletedFilter ? periodInDaysFilter : undefined)
    }

    return (
        <Grid container direction="column" gap="2rem" className={classes.container}>
            <LocalizedStrict id="supplier-account-gdpr-export-title">
                <Typography className={classes.title}>Download users</Typography>
            </LocalizedStrict>

            <UsersByDomainExport
                userTypesFilter={userTypesFilter}
                userDeletionPeriodFilter={periodInDaysFilter}
                includeDeletedFilter={includeDeletedFilter}
                setUserTypesFilter={setUserTypesFilter}
                setIncludeDeletedFilter={setIncludeDeletedFilter}
                setUserDeletionPeriodFilter={setPeriodInDaysFilter}
                exportedDomains={verifiedDomains}
            />

            <LocalizedStrict id="supplier-account-gdpr-export-info" attrs={{ description: true }}>
                <InfoBox description="This feature enables you to export administrators and/or users from all administrative units, including supplier, merchant, company, tenant, and service center. In order to download users from other administrative units, you must register one or more email domains associated with your company. These domains will be used to identify your company's users. If your company does not have any verified domains, you will be unable to download users from other units. For assistance with domain verification, please contact our Support Team." />
            </LocalizedStrict>

            <LocalizedStrict id="supplier-account-gdpr-export-export-button">
                <Button disabled={isLoading} onClick={handleExport} className={classes.button}>
                    Export to CSV
                </Button>
            </LocalizedStrict>
        </Grid>
    )
}
